import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Users, Shield, Building2, FileText, Eye, Settings2, Palette, Image, DollarSign, Database, Info, GitBranch, Phone, Rocket, Zap, Bell, Workflow, Volume2 } from 'lucide-react';

const SettingsCard = ({ title, description, icon: Icon, path, comingSoon = false }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      className={`p-6 hover:shadow-lg transition-all cursor-pointer relative ${
        comingSoon ? 'opacity-60' : ''
      }`}
      onClick={() => !comingSoon && navigate(path)}
      data-testid={`settings-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {comingSoon && (
        <div className="absolute top-4 right-4">
          <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded">Coming Soon</span>
        </div>
      )}
      <div className="flex items-start space-x-4">
        <div className="p-3 bg-primary/10 rounded-lg">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </Card>
  );
};

export const Settings = () => {
  const settingsModules = [
    {
      title: 'Automations',
      description: 'Build and manage automated workflows for your business',
      icon: Zap,
      path: '/settings/automations'
    },
    {
      title: '🔱 Prime Digest',
      description: 'Configure your daily morning digest time and preferences',
      icon: Bell,
      path: '/settings/prime-digest'
    },
    {
      title: 'Sound Notifications',
      description: 'Configure audio alerts for messages, logins, and events',
      icon: Volume2,
      path: '/settings/sounds'
    },
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions across the platform',
      icon: Users,
      path: '/settings/users'
    },
    {
      title: 'Teams',
      description: 'Create and manage teams, assign members and locations',
      icon: Users,
      path: '/settings/teams'
    },
    {
      title: 'Crews',
      description: 'Manage work crews for job assignments',
      icon: Users,
      path: '/settings/crews'
    },
    {
      title: 'Financing',
      description: 'Configure default financing options for estimates',
      icon: DollarSign,
      path: '/settings/financing'
    },
    {
      title: 'Company Settings',
      description: 'Configure company profile, branding, and general settings',
      icon: Building2,
      path: '/settings/company'
    },
    {
      title: 'Security & Audit',
      description: 'View audit logs, manage security settings and access control',
      icon: Shield,
      path: '/settings/security'
    },
    {
      title: 'AI Action Approvals',
      description: 'Review and approve AI agent actions, manage execution limits',
      icon: Zap,
      path: '/settings/action-approvals'
    },
    {
      title: 'Roles & Permissions',
      description: 'Configure role-based access control and permissions',
      icon: Shield,
      path: '/settings/roles'
    },
    {
      title: 'Onboarding',
      description: 'Configure user onboarding and new hire wizard workflows',
      icon: Users,
      path: '/settings/onboarding'
    },
    {
      title: 'Costs & Margins',
      description: 'Manage company costs, profit margins, and minimum requirements',
      icon: DollarSign,
      path: '/settings/costs-margins'
    },
    {
      title: 'Component Colors',
      description: 'Customize workflow stage colors and status badge colors',
      icon: Palette,
      path: '/settings/workflow-colors'
    },
    {
      title: 'Job Field Options',
      description: 'Configure dropdown options for job fields (warranty, products, etc.)',
      icon: Settings2,
      path: '/settings/job-fields'
    },
    {
      title: 'Data Tools',
      description: 'Import Builder Prime data and reset test data',
      icon: Database,
      path: '/settings/data-tools'
    },
    {
      title: 'Templates',
      description: 'Manage email, SMS, and document templates',
      icon: FileText,
      path: '/settings/templates'
    },
    {
      title: 'Integrations',
      description: 'Connect external services and manage API keys',
      icon: Shield,
      path: '/settings/integrations'
    },
    {
      title: 'Email Settings',
      description: 'Configure Gmail, Microsoft 365, and email sync settings',
      icon: Shield,
      path: '/settings/email'
    },
    {
      title: 'Onboarding',
      description: 'Configure the new user setup wizard and onboarding steps',
      icon: Rocket,
      path: '/settings/onboarding'
    },
    {
      title: 'Lead Flow Orchestrator',
      description: 'Configure board stages, hub ownership, and handoff rules',
      icon: GitBranch,
      path: '/settings/lead-flow'
    },
    {
      title: 'Demo Data Manager',
      description: 'Seed or wipe demo data for Call Center testing',
      icon: Phone,
      path: '/settings/demo-data'
    },
    {
      title: 'Custom Fields',
      description: 'Create and manage custom fields for jobs, leads, and more',
      icon: Settings2,
      path: '/settings/custom-fields'
    },
    {
      title: 'Theme Editor',
      description: 'Customize colors, typography, and visual styling',
      icon: Palette,
      path: '/theme-editor'
    },
    {
      title: 'Page Manager',
      description: 'Manage pages, navigation, and site structure',
      icon: FileText,
      path: '/page-manager'
    },
    {
      title: 'Content Manager',
      description: 'Edit dynamic content sections and page content',
      icon: FileText,
      path: '/content-manager'
    },
    {
      title: 'Branding Manager',
      description: 'Configure logos, brand assets, and visual identity',
      icon: Image,
      path: '/branding-manager'
    },
    {
      title: 'About Platform',
      description: 'View platform info, versioning, and RHINO-1 branding',
      icon: Info,
      path: '/settings/about'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your platform configuration and preferences</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {settingsModules.map((module, index) => (
            <SettingsCard key={index} {...module} />
          ))}
        </div>
      </div>
    </Layout>
  );
};